import moment from "moment";
import { Result, Section, ValidatorReview, ValidatorReviewAttempt, ValidatorReviewCorrection, ValidatorReviewIssue } from "./types";
export {
  buildEmptyFormRowFromTuple,
  getEmptyMultivalueRow,
  getMultivalueTupleSchema,
  logMultivalueDebug,
  resolveDocumentTypeNumber,
  resolveMultivalueTupleSchema,
  resolveTemplateDocumentType,
} from "./defaultMetadataByDocumentType";

// assume normalizeLineItemsSection(elements: Section[]) is in scope

export function rebuildSectionsFromFormData(
  originalSections: Section[],
  formData: Record<string, any>,
  path: Array<string | number> = []
): Section[] {
  // PRE: normalize any line_items_section -> multivalue/tuple
  const sections = normalizeLineItemsSection(originalSections);

  const getValue = (pathArr: Array<string | number>) => {
    let cur: any = formData;
    for (const key of pathArr) {
      if (cur && key in cur) cur = cur[key];
      else return undefined;
    }
    return cur;
  };

  return sections
    .map((section, index) => {
      const currentPath = [...path, section.schema_id || `index_${index}`];
      const newSection: Section = { ...section };

      if (section.category === "datapoint" || section.category === "field") {
        const value = getValue(currentPath);
        if (value !== undefined) newSection.value = value;
      } else if (section.category === "multivalue") {
        const arrayValue = getValue(currentPath);
        if (Array.isArray(arrayValue)) {
          // prefer an existing tuple schema as a template
          const tupleTemplate =
            section.children?.find((c) => c.category === "tuple") ??
            { category: "tuple", children: [] as Section[] };

          // @ts-ignore
          newSection.children = arrayValue.map((_, arrIndex) => ({
            ...tupleTemplate,
            // rebuild nested fields for this tuple index
            children: rebuildSectionsFromFormData(
              tupleTemplate.children || [],
              formData,
              [...currentPath, arrIndex]
            ),
          }));
        } else {
          newSection.children = [];
        }
      } else if (section.children) {
        newSection.children = rebuildSectionsFromFormData(
          section.children,
          formData,
          currentPath
        );
      }

      return newSection;
    })
    .filter(Boolean as any);
}

export type AssessmentReportRecordType = 'Deficiency__c' | 'Danger_of_deficiency_712__c' | 'Observation__c';

export type AssessmentReportItem = {
  description: string;
  recordType: AssessmentReportRecordType;
  result?: string;
};

// Mirrors the ASSESSMENT_REPORT_22943_CONTENT schema in defaultMetadataByDocumentType.ts —
// Landing_Page_Assessment_Report documents are extracted with the same field layout, just
// under a different DocumentType number (1 instead of 22943).
// resultFieldId only exists on line_items_section tuples (the deficiency "Klassifizierung").
const ASSESSMENT_ITEM_SECTIONS: Array<{
  sectionId: string;
  descriptionFieldId: string;
  resultFieldId?: string;
  recordType: AssessmentReportRecordType;
}> = [
  { sectionId: 'line_items_section', descriptionFieldId: 'item_description', resultFieldId: 'item_result_pick', recordType: 'Deficiency__c' },
  { sectionId: 'dangers_according_to_defect_712', descriptionFieldId: 'danger_description', recordType: 'Danger_of_deficiency_712__c' },
  { sectionId: 'observation_item_section', descriptionFieldId: 'observation_description', recordType: 'Observation__c' },
];

export function extractAssessmentReportItems(sections: Section[] = []): AssessmentReportItem[] {
  const items: AssessmentReportItem[] = [];

  ASSESSMENT_ITEM_SECTIONS.forEach(({ sectionId, descriptionFieldId, resultFieldId, recordType }) => {
    const section = sections.find(s => s.schema_id === sectionId);
    const multivalue = section?.children?.find(c => c.category === 'multivalue');

    multivalue?.children?.forEach(tuple => {
      const field = tuple.children?.find(f => f.schema_id === descriptionFieldId);
      const description = typeof field?.value === 'string' ? field.value.trim() : '';
      if (description) {
        const item: AssessmentReportItem = { description, recordType };
        if (resultFieldId) {
          const resultField = tuple.children?.find(f => f.schema_id === resultFieldId);
          const result = typeof resultField?.value === 'string' ? resultField.value.trim() : '';
          if (result) item.result = result;
        }
        items.push(item);
      }
    });
  });

  return items;
}

// zip_code lives on general_info_section, a document-level (not per-item) field.
export function extractAssessmentReportZipCode(sections: Section[] = []): string | undefined {
  const section = sections.find(s => s.schema_id === 'general_info_section');
  const field = section?.children?.find(f => f.schema_id === 'zip_code');
  const value = typeof field?.value === 'string' ? field.value.trim() : '';
  return value || undefined;
}



// --- Generic: normalize any line_items_section (no hardcoded IDs) ------
export const normalizeLineItemsSection = (elements: Section[]): Section[] => {
  const deepClone = <T>(v: T): T => JSON.parse(JSON.stringify(v));
  const cloned = deepClone(elements);

  const ensureTuples = (nodes: Section[] | undefined): Section[] => {
    if (!Array.isArray(nodes)) return [];
    // Convert each nested "section" into a "tuple" (wrap its children)
    // @ts-ignore
    return nodes
      .filter((n) => n && typeof n === 'object')
      .map((n, idx) => {
        if (n.category === 'tuple') return n; // already tuple
        if (n.category === 'section') {
          return {
            category: 'tuple' as const,
            schema_id: n.schema_id ?? `tuple_${idx}`,
            children: n.children ?? [],
          };
        }
        // If non-section nodes appear, wrap them as a single-field tuple for safety
        return {
          category: 'tuple' as const,
          schema_id: `tuple_${idx}`,
          children: [n],
        };
      });
  };

  const walk = (nodes: Section[]) => {
    for (const node of nodes) {
      // Target only line_items_section
      if (node.category === 'section' && node.schema_id === 'line_items_section' && Array.isArray(node.children)) {
        for (const child of node.children) {
          // If it's already multivalue with tuple children, leave it
          if (child.category === 'multivalue') {
            const allTuples = (child.children ?? []).every((c) => c.category === 'tuple');
            if (!allTuples) {
              child.children = ensureTuples(child.children);
            }
            continue;
          }

          // If it's a generic section, make it multivalue and convert its nested sections to tuples
          if (child.category === 'section') {
            // Only transform if it has children (repeated items)
            if (Array.isArray(child.children) && child.children.length > 0) {
              child.category = 'multivalue';
              child.children = ensureTuples(child.children);
            }
          }
        }
      }

      // Recurse through the rest of the tree
      if (Array.isArray(node.children)) walk(node.children);
    }
  };

  walk(cloned);
  return cloned;
};
// ----------------------------------------------------------------------

export const buildInitialFormData = (
  elements: Section[],
  initialData: any = {},
  path: (string | number)[] = []
) => {
  // PRE: normalize any line_items_section -> multivalue/tuple (generic)
  const normalized = normalizeLineItemsSection(elements);

  for (const element of normalized) {
    const currentPath = [...path, element.schema_id || `index_${normalized.indexOf(element)}`];

    if ((element.category === 'datapoint' || element.category === 'field') && element.value !== undefined) {
      const value = element.value;
      

      let current = initialData;
      currentPath.forEach((key, index) => {
        if (index === currentPath.length - 1) {
          current[key as any] = value === 'null' ? '' : value;
        } else {
          if (!current[key as any]) current[key as any] = {};
          current = current[key as any];
        }
      });
    } else if (element.category === 'multivalue') {
      let current = initialData;
      currentPath.forEach((key, index) => {
        if (index === currentPath.length - 1) {
          if (!Array.isArray(current[key as any])) current[key as any] = [];
        } else {
          if (!current[key as any]) current[key as any] = {};
          current = current[key as any];
        }
      });

      // Each child is (now) a tuple; build for each index
      (element.children ?? []).forEach((tuple, idx) => {
        if (!current[currentPath[currentPath.length - 1] as any][idx]) {
          current[currentPath[currentPath.length - 1] as any][idx] = {};
        }
        buildInitialFormData(tuple.children ?? [], initialData, [...currentPath, idx]);
      });
    } else if (element.children) {
      buildInitialFormData(element.children, initialData, currentPath);
    }
  }
  return initialData;
};

export function addRequiredSections(
  doc: Result,
  sectionRequirements: Array<{
    sectionSchemaId: string;
    childSchemaIds: string[];
  }>
): Result {
  const sectionMap = new Map<string, Set<string>>();
  
  // Create a map for quick lookup
  sectionRequirements.forEach(({ sectionSchemaId, childSchemaIds }) => {
    sectionMap.set(sectionSchemaId, new Set(childSchemaIds));
  });

  const markTargets = (node, targets: Set<string>) => {
    const isTarget = node.schema_id && targets.has(node.schema_id);
    const updated = isTarget ? { ...node, required: true } : { ...node };
    if (updated.children?.length) updated.children = updated.children.map(child => 
      markTargets(child, targets)
    );
    return updated;
  };

  const visit = (node) => {
    // Check if this section has requirements
    if (node.category === "section" && sectionMap.has(node.schema_id)) {
      const targets = sectionMap.get(node.schema_id)!;
      const updated = { ...node };
      if (updated.children?.length) {
        updated.children = updated.children.map(child => 
          markTargets(child, targets)
        );
      }
      return updated;
    }
    
    const cloned = { ...node };
    if (cloned.children?.length) cloned.children = cloned.children.map(visit);
    return cloned;
  };

  return {
    ...doc,
    content: doc.content.map(visit),
  };
}

/**
 * Strips non-numeric characters from number-type datapoints (e.g. "19 %" → "19").
 */
export function sanitizeNumberFields(doc: Result): Result {
  const visit = (node: any): any => {
    const cloned = { ...node };

    if (
      (cloned.category === 'datapoint' || cloned.category === 'field') &&
      cloned.type === 'number' &&
      typeof cloned.value === 'string'
    ) {
      const numeric = cloned.value.replace(/[^0-9.\-]/g, '');
      cloned.value = numeric;
    }

    if (cloned.children?.length) {
      cloned.children = cloned.children.map(visit);
    }

    return cloned;
  };

  return {
    ...doc,
    content: doc.content.map(visit),
  };
}

/**
 * Clears multivalue children to [] when all tuples contain only empty/zero-value datapoints.
 * The section structure is preserved so items can still be added dynamically.
 */
export function clearEmptyMultivalueSections(doc: Result): Result {
  const isEmptyValue = (value: any): boolean => {
    if (value === undefined || value === null || value === '') return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (value === '0' || value === 0) return true;
    return false;
  };

  const allDatapointsEmpty = (children: any[]): boolean => {
    return children.every(child => {
      if (child.category === 'datapoint' || child.category === 'field') {
        return isEmptyValue(child.value);
      }
      if (child.children?.length) {
        return allDatapointsEmpty(child.children);
      }
      return true;
    });
  };

  const visit = (node: any): any => {
    const cloned = { ...node };

    if (cloned.category === 'multivalue' && Array.isArray(cloned.children)) {
      const allEmpty = cloned.children.every(tuple =>
        allDatapointsEmpty(tuple.children || [])
      );
      if (allEmpty) {
        // Preserve the first tuple as a template so DynamicForm can use it for adding new rows
        const tupleTemplate = cloned.children.find((c: any) => c.category === 'tuple');
        cloned._tupleTemplate = tupleTemplate ? JSON.parse(JSON.stringify(tupleTemplate)) : null;
        cloned.children = [];
        return cloned;
      }
    }

    if (cloned.children?.length) {
      cloned.children = cloned.children.map(visit);
    }

    return cloned;
  };

  return {
    ...doc,
    content: doc.content.map(visit),
  };
}

export function parseDate(dateStr: string | null): Date | null {
  if (!dateStr) return null;

  // Handle DD.MM.YYYY format (e.g. "16.10.2025")
  const europeanMatch = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (europeanMatch) {
    const [_, day, month, year] = europeanMatch;
    return new Date(`${year}-${month}-${day}`);
  }

  // Handle ISO or other valid formats
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

export function filterData(data: any[], filterOptions: any): any[] {
  return data.filter(doc => {
    return Object.entries(filterOptions).every(([key, value]) => {
      if (key === 'filename') {
        return doc.filename.toLowerCase().includes((value as string)?.trim().toLowerCase());
      }
      if (key === 'account') {
        return doc.account.toLowerCase().includes((value as string)?.trim().toLowerCase());
      }
      if (key === 'type') {
        return doc.type.toLowerCase().includes((value as string)?.trim()?.toLowerCase().replace(' ', '_'));
      }
      if (key === 'senderReceiver') {
        return doc.senderReceiver.toLowerCase().includes((value as string)?.trim().toLowerCase());
      }

      if (key === 'uploadedAt') {
        return moment(doc.uploadedAt).isSame(moment(value), 'day');
      }

      if (key === 'documentDate') {
        const docDate = doc?.documentDate;
        if (!docDate) return false;
        return moment(docDate).isSame(moment(value), 'day');
      }

      if (key === 'fabrikNumber') {
        return doc.fabrikNumber.toLowerCase().includes((value as string)?.trim().toLowerCase());
      }

      if (key === 'gstoreId') {
        const gstoreId = String(doc.gstoreId ?? doc.jsonContent?.GStoreId ?? '').toLowerCase();
        return gstoreId.includes((value as string)?.trim().toLowerCase());
      }

      if (key === 'senderReceiverAddress') {
        return doc.senderReceiverAddress.toLowerCase().includes((value as string)?.trim().toLowerCase());
      }

      return true;
    });
  });
}

const JSON_ID_COLLECTION_BY_TAB: Record<string, string> = {
  '8': 'investigation',
};

export function resolveJsonIdForTab(jsonId: string | undefined, pdfPath: string | undefined, tabKey: string): string {
  const collection = JSON_ID_COLLECTION_BY_TAB[tabKey];
  if (!collection) {
    return jsonId ?? '';
  }

  if (jsonId?.startsWith(`${collection}/`)) {
    return jsonId;
  }

  const source = jsonId || pdfPath || '';
  const fileName = source.split('/').pop() ?? source;
  const jsonFileName = fileName.endsWith('.json')
    ? fileName
    : `${fileName.replace(/\.pdf$/i, '')}.json`;

  return `${collection}/${jsonFileName}`;
}

export function resolveValidatorReview(
  pdfDetails: { validator_review?: unknown; jsonContent?: { validator_review?: unknown } } | null,
  selectedDocument?: { jsonContent?: { validator_review?: unknown } } | null,
): ValidatorReview | null {
  const fromPdf = pdfDetails?.validator_review;
  const fromPdfJson = pdfDetails?.jsonContent?.validator_review;
  const fromSelected = (selectedDocument?.jsonContent as { validator_review?: unknown } | undefined)?.validator_review;

  const review = fromPdf ?? fromPdfJson ?? fromSelected;
  if (!review || typeof review !== 'object') {
    return null;
  }

  return normalizeValidatorReview(review as Partial<ValidatorReview>);
}

function normalizeValidatorReviewIssue(issue: Partial<ValidatorReviewIssue>): ValidatorReviewIssue {
  return {
    code: issue.code ?? '',
    message: issue.message ?? '',
    json_path: issue.json_path ?? '',
    severity: issue.severity ?? 'warning',
    ...(issue.locale_key ? { locale_key: issue.locale_key } : {}),
    ...(issue.schema_id ? { schema_id: issue.schema_id } : {}),
    ...(issue.section_schema_id ? { section_schema_id: issue.section_schema_id } : {}),
    ...(issue.line_item_index !== undefined ? { line_item_index: issue.line_item_index } : {}),
    ...(issue.agent_field ? { agent_field: issue.agent_field } : {}),
    ...(issue.agent_block ? { agent_block: issue.agent_block } : {}),
  };
}

function normalizeValidatorReviewCorrection(
  correction: Partial<ValidatorReviewCorrection>,
): ValidatorReviewCorrection {
  return {
    json_path: correction.json_path ?? '',
    field: correction.field ?? '',
    issue: correction.issue ?? '',
    instruction: correction.instruction ?? '',
    expected_type: correction.expected_type ?? '',
    allowed_values: correction.allowed_values ?? [],
    suggested_value: correction.suggested_value ?? null,
    confidence: correction.confidence ?? 0,
    auto_apply: correction.auto_apply ?? false,
    ...(correction.locale_key ? { locale_key: correction.locale_key } : {}),
    ...(correction.schema_id ? { schema_id: correction.schema_id } : {}),
    ...(correction.section_schema_id ? { section_schema_id: correction.section_schema_id } : {}),
    ...(correction.line_item_index !== undefined ? { line_item_index: correction.line_item_index } : {}),
    ...(correction.agent_field ? { agent_field: correction.agent_field } : {}),
    ...(correction.agent_block ? { agent_block: correction.agent_block } : {}),
  };
}

function normalizeValidatorReviewAttempt(attempt: Partial<ValidatorReviewAttempt>): ValidatorReviewAttempt {
  return {
    schema_version: attempt.schema_version ?? '1.0',
    valid: attempt.valid ?? false,
    auto_submit_eligible: attempt.auto_submit_eligible ?? false,
    confidence: attempt.confidence ?? 0,
    review_route: attempt.review_route ?? '',
    summary: attempt.summary ?? '',
    blocking_issues: (attempt.blocking_issues ?? []).map(normalizeValidatorReviewIssue),
    warnings: (attempt.warnings ?? []).map(normalizeValidatorReviewIssue),
    corrections: (attempt.corrections ?? []).map(normalizeValidatorReviewCorrection),
    retry: attempt.retry ?? { eligible: false, reason: '', agent_prompt_addendum: '' },
    attempt_number: attempt.attempt_number ?? 0,
    max_attempts: attempt.max_attempts ?? 0,
  };
}

export function normalizeValidatorReview(review: Partial<ValidatorReview>): ValidatorReview {
  return {
    schema_version: review.schema_version ?? '1.0',
    valid: review.valid ?? false,
    auto_submit_eligible: review.auto_submit_eligible ?? false,
    confidence: review.confidence ?? 0,
    review_route: review.review_route ?? '',
    summary: review.summary ?? '',
    blocking_issues: (review.blocking_issues ?? []).map(normalizeValidatorReviewIssue),
    warnings: (review.warnings ?? []).map(normalizeValidatorReviewIssue),
    corrections: (review.corrections ?? []).map(normalizeValidatorReviewCorrection),
    retry: review.retry ?? { eligible: false, reason: '', agent_prompt_addendum: '' },
    attempt_number: review.attempt_number ?? 0,
    max_attempts: review.max_attempts ?? 0,
    validation_attempts: (review.validation_attempts ?? []).map(normalizeValidatorReviewAttempt),
    salesforce_result: review.salesforce_result,
  };
}
